import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERF_RECOMMENDATIONS,
  PERF_RECOMMENDATION_MAP,
  REVIEW_STATUSES,
  REVIEW_STATUS_MAP,
  type PerfRecommendation,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import type { CycleRow, GoalRow } from './Performance';

interface Criterion {
  key: string;
  label: string;
  weight: number;
  description?: string;
}

interface CycleView extends CycleRow {
  criteria: Criterion[];
  weightTotal: number;
  scored: number;
  distribution: { key: string; label: string; kind: string; count: number }[];
}

export interface ReviewRow {
  id: string;
  status: 'DRAFT' | 'SUBMITTED' | 'ACKNOWLEDGED';
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  counted: boolean;
  overall: number | null;
  scaleMax: number;
  band: string | null;
  bandLabel: string | null;
  bandKind: string | null;
  strengths: string | null;
  improvements: string | null;
  summary: string | null;
  recommendation: PerfRecommendation | null;
  recommendationLabel: string | null;
  recommendationKind: string | null;
  recommendationAction: string | null;
  recommendationPath: string | null;
  submittedAt: string | null;
  acknowledgedAt: string | null;
  cycle: { id: string; name: string; scaleMax: number };
  employee: {
    id: string;
    name: string;
    code: string | null;
    jobTitle: string | null;
    department: string | null;
  };
  reviewer: { id: string; name: string } | null;
}

interface ReviewView extends ReviewRow {
  criteria: Criterion[];
  scores: Record<string, number>;
  cycleStatus: 'DRAFT' | 'OPEN' | 'CLOSED';
  cycleStatusLabel: string;
  goals: GoalRow[];
  employeeNote: string | null;
}

interface DepartmentsView {
  scaleMax: number;
  minSample: number;
  rows: {
    department: string;
    count: number;
    average: number | null;
    band: string | null;
    reason: string | null;
  }[];
}

/**
 * دورةُ تقييمٍ واحدة.
 *
 * ## ولماذا تُجمَّد المعاييرُ عند الفتح
 *
 * درجاتُ التقييم مخزَّنةٌ بمفاتيح المعايير. ومعيارٌ يُحذف بعد أن كُتبت عليه
 * درجاتٌ يترك أرقامًا لا يُعرف ما تقيس، ومعيارٌ يُضاف بعد أن أرسل نصفُ
 * المديرين تقييماتِهم يجعل النصفَ الأوّلَ مقيسًا على غير ما قيس به الثاني
 * — ثمّ يُقارَن الاثنان في جدولٍ واحد.
 *
 * ## والإغلاقُ في اتّجاهٍ واحد
 *
 * مغلقةٌ لا تُفتح: تقييماتٌ أُبلغ أصحابُها بها لا تُعدَّل من ورائهم. ومن
 * أغلق قبل أوانه يفتح دورةً جديدةً لما بقي.
 */
export function PerformanceCycle() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [statusFilter, setStatusFilter] = useState('');
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reviewId, setReviewId] = useState<string | null>(null);

  const cycle = useQuery({
    queryKey: ['performance', 'cycle', id],
    queryFn: () => api<CycleView>(`/hr/performance/cycles/${id}`),
  });

  const query = { cycleId: id, status: statusFilter || undefined };

  const reviews = useQuery({
    queryKey: ['performance', 'reviews', query],
    queryFn: () => api<ReviewRow[]>(`/hr/performance/reviews${qs(query)}`),
  });

  const departments = useQuery({
    queryKey: ['performance', 'departments', id],
    queryFn: () =>
      api<DepartmentsView>(`/hr/performance/cycles/${id}/departments`),
    enabled: cycle.data?.status !== 'DRAFT',
  });

  const setStatus = useMutation({
    mutationFn: (status: 'OPEN' | 'CLOSED') =>
      api(`/hr/performance/cycles/${id}/status`, {
        method: 'POST',
        body: { status },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
      setOpening(false);
      setClosing(false);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const seed = useMutation({
    mutationFn: () =>
      api<{ created: number; total: number }>(
        `/hr/performance/cycles/${id}/seed`,
        { method: 'POST' },
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
      toast(
        r.created === 0
          ? 'لكلّ من على رأس العمل تقييمٌ في هذه الدورة بالفعل.'
          : `فُتح ${plural(r.created, WORDS.review)}.`,
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const c = cycle.data;
  const rows = reviews.data ?? [];

  return (
    <div className="page">
      <PageHead
        title={c?.name ?? 'دورة تقييم'}
        subtitle={
          c
            ? `${date(c.startsOn ?? '')} — ${date(c.endsOn ?? '')} · سُلَّمٌ من ${num(c.scaleMax)}`
            : undefined
        }
        actions={
          canEdit &&
          c && (
            <div className="row" style={{ gap: 6 }}>
              {c.status === 'DRAFT' && (
                <button
                  className="btn btn-primary"
                  onClick={() => setOpening(true)}
                >
                  <Icon name="rocket" size={14} />
                  افتح الدورة
                </button>
              )}
              {c.status === 'OPEN' && (
                <>
                  <button
                    className="btn"
                    disabled={seed.isPending}
                    onClick={() => seed.mutate()}
                  >
                    <Icon name="users" size={14} />
                    افتح تقييمًا لكلّ موظّف
                  </button>
                  <button className="btn" onClick={() => setClosing(true)}>
                    أغلق الدورة
                  </button>
                </>
              )}
            </div>
          )
        }
      />

      <Link className="link tiny" to="/hr/performance">
        <Icon name="arrowLeft" size={12} /> إدارة الأداء
      </Link>

      {cycle.isError && (
        <ErrorState error={cycle.error} onRetry={() => void cycle.refetch()} />
      )}
      {cycle.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {c && (
        <>
          <div className="stats mt-3">
            <Stat
              label="الحالة"
              value={<Badge kind={c.statusKind}>{c.statusLabel}</Badge>}
              note={c.statusNote}
            />
            <Stat
              label="المعايير"
              value={num(c.criteriaCount)}
              note={`مجموع الأوزان ${num(c.weightTotal)}٪`}
            />
            <Stat
              label="التقييمات"
              value={num(c.reviews)}
              note={`منها ${num(c.scored)} أُرسل ولها محصّلة`}
            />
            <Stat
              label="الأهداف"
              value={num(c.goals)}
              note="مكتوبةٌ في هذه الدورة"
            />
          </div>

          {c.status === 'DRAFT' && (
            <Notice title="الدورةُ تُعدّ بعد" icon="info">
              المعاييرُ والسُّلَّمُ تُحرَّر ما دامت مسوّدة. وبفتحها تُجمَّد:
              درجاتُ التقييمات تُكتب بمفاتيح المعايير، ومعيارٌ يتغيّر بعدها يترك
              أرقامًا لا يُعرف ما تقيس.
            </Notice>
          )}

          {c.status === 'CLOSED' && (
            <Notice title="الدورةُ مغلقة" icon="check" kind="success">
              ما فيها تاريخٌ لا يُعدَّل، ولا تُفتح بعد إغلاقها. ومن أراد أن يقيس
              ما بعدها يفتح دورةً جديدة.
            </Notice>
          )}

          {/* ---------- المعايير ---------- */}
          <div className="card mt-3">
            <h2 className="card-title">المعايير</h2>
            {c.criteria.length === 0 ? (
              <EmptyState
                title="لا معايير"
                text="دورةٌ بلا معاييرَ تُنتج تقييماتٍ بلا درجات — ورأيًا مكتوبًا بلا سند."
              />
            ) : (
              <div className="table-wrap mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>المعيار</th>
                      <th>الوزن</th>
                      <th>ما يُقاس به</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.criteria.map((x) => (
                      <tr key={x.key}>
                        <td>{x.label}</td>
                        <td className="num">
                          {x.weight > 0 ? `${num(x.weight)}٪` : '—'}
                        </td>
                        <td className="tiny mute">{x.description ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {c.weightTotal === 0 && c.criteria.length > 0 && (
              <p className="tiny mute mt-2">
                لا أوزانَ مكتوبة — فالمحصّلةُ متوسّطٌ بسيطٌ للدرجات.
              </p>
            )}
          </div>

          {/* ---------- التوزيع ---------- */}
          {c.distribution.length > 0 && (
            <div className="card mt-3">
              <h2 className="card-title">توزيع المراتب</h2>
              <div
                className="row mt-2"
                style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}
              >
                {c.distribution.map((d) => (
                  <div key={d.key} className="stat" style={{ minWidth: 140 }}>
                    <div className="stat-label">{d.label}</div>
                    <div className="stat-value">{num(d.count)}</div>
                  </div>
                ))}
              </div>
              {/*
                يُقرأ من المُرسَل وحدَه: المسوّداتُ نصفُ مكتوبةٍ بطبعها،
                وعدُّها يجعل الدورةَ تبدو كأنّ أداءها منهار.
              */}
              <p className="tiny mute mt-2">
                من التقييمات المُرسَلة وحدَها — والمسوّدةُ لا تُحسب في شيء.
                والمرتبةُ تُقاس بنسبة المحصّلة من أعلى السُّلَّم، فتُقارَن
                دورتان بسُلَّمين مختلفين.
              </p>
            </div>
          )}

          {/* ---------- الأقسام ---------- */}
          {departments.data && departments.data.rows.length > 0 && (
            <div className="card mt-3">
              <h2 className="card-title">المتوسّط بالقسم</h2>
              <div className="table-wrap mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>القسم</th>
                      <th>التقييمات</th>
                      <th>المتوسّط</th>
                      <th>المرتبة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.data.rows.map((r) => (
                      <tr key={r.department}>
                        <td>{r.department}</td>
                        <td className="num">{num(r.count)}</td>
                        <td className="num">
                          {r.average === null ? '—' : num(r.average)}
                        </td>
                        <td className="tiny mute">
                          {r.reason ?? (r.band ? r.band : '—')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="tiny mute mt-2">
                لا يُعرض متوسّطٌ لقسمٍ تحت {num(departments.data.minSample)}{' '}
                تقييمات: متوسّطُ ثلاثةٍ يكشف أصحابَه لمن يعرف عددَهم، ويُقرأ
                حكمًا على إدارةٍ وهو حكمٌ على ثلاثة.
              </p>
            </div>
          )}

          {/* ---------- التقييمات ---------- */}
          <div className="card mt-3">
            <div className="row" style={{ gap: 'var(--sp-2)' }}>
              <h2 className="card-title" style={{ flex: 1 }}>
                التقييمات
              </h2>
              <select
                className="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">كل الحالات</option>
                {REVIEW_STATUSES.map((k) => (
                  <option key={k} value={k}>
                    {REVIEW_STATUS_MAP[k].label}
                  </option>
                ))}
              </select>
            </div>

            {reviews.isPending && (
              <div className="skeleton" style={{ height: 160 }} />
            )}

            {reviews.data && rows.length === 0 && (
              <EmptyState
                title="لا تقييمات"
                text={
                  c.status === 'OPEN'
                    ? 'افتح تقييمًا لكلّ من على رأس العمل بزرٍّ واحد — ويُسنَد كلٌّ إلى مديره في ملفّه.'
                    : 'التقييماتُ تُكتب في دورةٍ مفتوحةٍ وحدَها.'
                }
              />
            )}

            {rows.length > 0 && (
              <div className="table-wrap sticky-head mt-3">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الموظّف</th>
                      <th>يكتبه</th>
                      <th>المحصّلة</th>
                      <th>المرتبة</th>
                      <th>التوصية</th>
                      <th>الحالة</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.employee.name}
                          {r.employee.jobTitle && (
                            <div className="tiny mute">
                              {r.employee.jobTitle}
                            </div>
                          )}
                        </td>
                        <td className="tiny">{r.reviewer?.name ?? '—'}</td>
                        <td className="num">
                          {r.overall === null
                            ? '—'
                            : `${num(r.overall)} / ${num(r.scaleMax)}`}
                        </td>
                        <td>
                          {r.bandLabel ? (
                            <Badge kind={r.bandKind ?? 'neutral'}>
                              {r.bandLabel}
                            </Badge>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="tiny">{r.recommendationLabel ?? '—'}</td>
                        <td>
                          <Badge kind={r.statusKind}>{r.statusLabel}</Badge>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm"
                            onClick={() => setReviewId(r.id)}
                          >
                            افتحه
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {opening && (
        <Modal
          title="فتح الدورة"
          onClose={() => setOpening(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpening(false)}>
                إلغاء
              </button>
              <button
                className="btn btn-primary"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate('OPEN')}
              >
                افتحها
              </button>
            </>
          }
        >
          <Notice title="ما يقع عند الفتح" icon="alert" kind="warning">
            تُجمَّد المعاييرُ والسُّلَّم فلا يُمسّان بعدها، وتُفتح كتابةُ
            التقييمات. والاسمُ والمدّةُ تبقى قابلةً للتصحيح.
          </Notice>
        </Modal>
      )}

      {closing && (
        <Modal
          title="إغلاق الدورة"
          onClose={() => setClosing(false)}
          footer={
            <>
              <button className="btn" onClick={() => setClosing(false)}>
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                disabled={setStatus.isPending}
                onClick={() => setStatus.mutate('CLOSED')}
              >
                أغلقها
              </button>
            </>
          }
        >
          <Notice title="والإغلاقُ لا رجعةَ فيه" icon="alert" kind="danger">
            مغلقةٌ لا تُفتح: تقييماتٌ أُبلغ أصحابُها بها لا تُعدَّل من ورائهم.
            والمسوّداتُ التي لم تُرسل تبقى مسوّداتٍ إلى الأبد — أرسِلها قبل
            الإغلاق أو اتركها تشهد على أنّها لم تُكتب.
          </Notice>
        </Modal>
      )}

      {reviewId && (
        <ReviewDialog
          reviewId={reviewId}
          canEdit={canEdit}
          onClose={() => setReviewId(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   نافذة التقييم
   ========================================================================== */

function ReviewDialog({
  reviewId,
  canEdit,
  onClose,
}: {
  reviewId: string;
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const review = useQuery({
    queryKey: ['performance', 'review', reviewId],
    queryFn: () => api<ReviewView>(`/hr/performance/reviews/${reviewId}`),
  });

  const r = review.data;

  return (
    <Modal
      title={r ? `تقييم ${r.employee.name}` : 'تقييم'}
      onClose={onClose}
      wide
    >
      {review.isPending && <div className="skeleton" style={{ height: 260 }} />}
      {review.isError && (
        <ErrorState
          error={review.error}
          onRetry={() => void review.refetch()}
        />
      )}
      {r && (
        <ReviewForm
          review={r}
          canEdit={canEdit}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['performance'] });
          }}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['performance'] });
            toast('حُفظ التقييم.');
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

function ReviewForm({
  review,
  canEdit,
  onSaved,
  onDone,
}: {
  review: ReviewView;
  canEdit: boolean;
  onSaved: () => void;
  onDone: () => void;
}) {
  const toast = useToast();

  const [scores, setScores] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      review.criteria.map((c) => [
        c.key,
        review.scores[c.key] === undefined ? '' : String(review.scores[c.key]),
      ]),
    ),
  );
  const [strengths, setStrengths] = useState(review.strengths ?? '');
  const [improvements, setImprovements] = useState(review.improvements ?? '');
  const [summary, setSummary] = useState(review.summary ?? '');
  const [recommendation, setRecommendation] = useState<string>(
    review.recommendation ?? '',
  );
  const [note, setNote] = useState('');

  const writable =
    canEdit && review.status === 'DRAFT' && review.cycleStatus === 'OPEN';

  const body = () => ({
    scores: Object.fromEntries(
      Object.entries(scores)
        .filter(([, v]) => v !== '')
        .map(([k, v]) => [k, Number(v)]),
    ),
    strengths: strengths || null,
    improvements: improvements || null,
    summary: summary || null,
    recommendation: recommendation || null,
  });

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/performance/reviews/${review.id}`, {
        method: 'PATCH',
        body: body(),
      }),
    onSuccess: () => {
      onSaved();
      toast('حُفظت المسوّدة.');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const submit = useMutation({
    mutationFn: async () => {
      await api(`/hr/performance/reviews/${review.id}`, {
        method: 'PATCH',
        body: body(),
      });
      return api(`/hr/performance/reviews/${review.id}/status`, {
        method: 'POST',
        body: { status: 'SUBMITTED' },
      });
    },
    onSuccess: onDone,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const reopen = useMutation({
    mutationFn: () =>
      api(`/hr/performance/reviews/${review.id}/status`, {
        method: 'POST',
        body: { status: 'DRAFT' },
      }),
    onSuccess: onDone,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const acknowledge = useMutation({
    mutationFn: () =>
      api(`/hr/performance/reviews/${review.id}/status`, {
        method: 'POST',
        body: { status: 'ACKNOWLEDGED', employeeNote: note || null },
      }),
    onSuccess: onDone,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const rec = recommendation
    ? PERF_RECOMMENDATION_MAP[recommendation as PerfRecommendation]
    : null;

  return (
    <>
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <Badge kind={review.statusKind}>{review.statusLabel}</Badge>
        <span className="tiny mute" style={{ flex: 1 }}>
          {review.statusNote}
        </span>
        {review.overall !== null && (
          <span className="tiny">
            المحصّلة: {num(review.overall)} / {num(review.scaleMax)}
            {review.bandLabel ? ` · ${review.bandLabel}` : ''}
          </span>
        )}
      </div>

      {/* ---------- أهدافُه في الدورة ---------- */}
      {review.goals.length > 0 && (
        <div className="mt-3">
          <h3 className="card-title">أهدافُه في هذه الدورة</h3>
          <div className="table-wrap mt-1">
            <table className="table">
              <thead>
                <tr>
                  <th>الهدف</th>
                  <th>المستهدَف</th>
                  <th>ما بلغه</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {review.goals.map((g) => (
                  <tr key={g.id}>
                    <td>{g.title}</td>
                    <td className="tiny">{g.target ?? '—'}</td>
                    <td className="num">{num(g.progress)}٪</td>
                    <td>
                      <Badge kind={g.statusKind}>{g.statusLabel}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/*
            تُقرأ مع التقييم لا بعده: تقييمٌ يُكتب بلا نظرٍ إلى ما اتُّفق
            عليه أوّلَ السنة رأيٌ في شخصٍ لا حكمٌ على عمل.
          */}
          <p className="tiny mute mt-1">
            ما اتُّفق عليه أوّلَ المدّة — يُقرأ قبل أن تُكتب الدرجات.
          </p>
        </div>
      )}

      {review.goals.length === 0 && review.cycleStatus === 'OPEN' && (
        <Notice title="لا أهدافَ مكتوبةً له في هذه الدورة" icon="info">
          تقييمٌ بلا أهدافٍ سابقةٍ حكمٌ على انطباع. واكتبْ له أهدافًا من شاشة
          إدارة الأداء ليُقاس عليها في الدورة القادمة.
        </Notice>
      )}

      {/* ---------- الدرجات ---------- */}
      <div className="mt-3">
        <h3 className="card-title">الدرجات</h3>
        <div className="table-wrap mt-1">
          <table className="table">
            <thead>
              <tr>
                <th>المعيار</th>
                <th>الوزن</th>
                <th>الدرجة من {num(review.scaleMax)}</th>
              </tr>
            </thead>
            <tbody>
              {review.criteria.map((c) => (
                <tr key={c.key}>
                  <td>
                    {c.label}
                    {c.description && (
                      <div className="tiny mute">{c.description}</div>
                    )}
                  </td>
                  <td className="num">
                    {c.weight > 0 ? `${num(c.weight)}٪` : '—'}
                  </td>
                  <td style={{ width: 120 }}>
                    <input
                      className="input num"
                      type="number"
                      min={1}
                      max={review.scaleMax}
                      step={0.5}
                      disabled={!writable}
                      value={scores[c.key] ?? ''}
                      onChange={(e) =>
                        setScores({ ...scores, [c.key]: e.target.value })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="form-grid mt-3">
        <div className="field span-2">
          <label className="label" htmlFor="pr-strengths">
            ما أحسنه
          </label>
          <textarea
            id="pr-strengths"
            className="input"
            rows={2}
            disabled={!writable}
            value={strengths}
            onChange={(e) => setStrengths(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="pr-improve">
            ما يحتاج أن يحسّنه
          </label>
          <textarea
            id="pr-improve"
            className="input"
            rows={2}
            disabled={!writable}
            value={improvements}
            onChange={(e) => setImprovements(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="pr-summary">
            الخلاصة
          </label>
          <textarea
            id="pr-summary"
            className="input"
            rows={3}
            disabled={!writable}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="pr-rec">
            التوصية
          </label>
          <select
            id="pr-rec"
            className="select"
            disabled={!writable}
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
          >
            <option value="">— بلا توصية —</option>
            {PERF_RECOMMENDATIONS.map((k) => (
              <option key={k} value={k}>
                {PERF_RECOMMENDATION_MAP[k].label}
              </option>
            ))}
          </select>
          {/*
            التوصيةُ تُسجَّل ولا تُنفَّذ: زرٌّ اسمُه «رقِّه» يعني أن يرفع نصٌّ
            في خانةٍ راتبَ رجلٍ بلا أن يقرّه أحد.
          */}
          {rec && (
            <span className="tiny mute">
              {rec.description}
              {rec.actionLabel && rec.actionPath ? (
                <>
                  {' '}
                  — يُنفَّذ في بابه:{' '}
                  <Link className="link" to={rec.actionPath}>
                    {rec.actionLabel}
                  </Link>
                  .
                </>
              ) : null}
            </span>
          )}
          {!rec && (
            <span className="tiny mute">
              ما يُوصى به يُسجَّل هنا ويُنفَّذ في بابه — لا من هذه الشاشة.
            </span>
          )}
        </div>
      </div>

      {review.employeeNote && (
        <Notice title="تعليقُ صاحب التقييم" icon="chat">
          {review.employeeNote}
        </Notice>
      )}

      {/* ---------- الإجراءات ---------- */}
      <div className="row mt-3" style={{ gap: 6, justifyContent: 'flex-end' }}>
        {writable && (
          <>
            <button
              className="btn"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              احفظ مسوّدة
            </button>
            <button
              className="btn btn-primary"
              disabled={submit.isPending}
              onClick={() => submit.mutate()}
            >
              <Icon name="send" size={14} />
              أرسله
            </button>
          </>
        )}

        {canEdit &&
          review.status === 'SUBMITTED' &&
          review.cycleStatus === 'OPEN' && (
            <>
              <button
                className="btn"
                disabled={reopen.isPending}
                onClick={() => reopen.mutate()}
              >
                أعِده مسوّدة
              </button>
              <button
                className="btn btn-primary"
                disabled={acknowledge.isPending}
                onClick={() => acknowledge.mutate()}
              >
                <Icon name="check" size={14} />
                سجّل إقرارَ صاحبه
              </button>
            </>
          )}
      </div>

      {canEdit &&
        review.status === 'SUBMITTED' &&
        review.cycleStatus === 'OPEN' && (
          <div className="field mt-2">
            <label className="label" htmlFor="pr-note">
              تعليقُ صاحبه عند الإقرار
            </label>
            <textarea
              id="pr-note"
              className="input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ما قاله حين قرأ تقييمه — يُكتب كما قاله"
            />
            {/*
              الإقرارُ يُسجَّل بيدٍ لأنّ الموظّف لا يفتح هذه الشاشة: هي
              لمدير النظام. وادّعاءُ توقيعٍ إلكترونيٍّ من حسابٍ غيرِ حسابه
              ادّعاءٌ لا يصمد أمام أوّل نزاع — فيُسجَّل أنّ فلانًا سجّل أنّه
              أقرّ، وذاك ما وقع.
            */}
            <span className="tiny mute">
              يُسجَّل هنا بيدٍ أنّ صاحبَه قرأ تقييمَه — لا توقيعَ إلكترونيًّا في
              هذا النظام، وما يُحفظ أنّ من يكتب سجّل الواقعة.
            </span>
          </div>
        )}

      {review.cycleStatus !== 'OPEN' && (
        <p className="tiny mute mt-2">
          الدورة {review.cycleStatusLabel} — لا يُكتب فيها ولا يُرسل.
        </p>
      )}
    </>
  );
}
